// Royalty-free food photos (Unsplash License — commercial use, no attribution).
// Used as attractive placeholders; the client can replace with real photos of
// their own dishes via the admin photo upload. Every usage has an emoji/gradient
// fallback (see SmartImage) so a failed URL never shows a broken image.
import type { Category } from "../lib/types";

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1781334266250-a7e72fdf539f?auto=format&fit=crop&w=1200&q=70";

export const CATEGORY_IMAGES: Partial<Record<Category, string>> = {
  chinese:
    "https://images.unsplash.com/photo-1585032226651-759b368d7246?auto=format&fit=crop&w=800&q=70",
  seafood:
    "https://images.unsplash.com/photo-1605493624448-8efd97c98c91?auto=format&fit=crop&w=800&q=70",
  veg: "https://images.unsplash.com/photo-1627366422957-3efa9c6df0fc?auto=format&fit=crop&w=800&q=70",
  breads:
    "https://images.unsplash.com/photo-1697155406014-04dc649b0953?auto=format&fit=crop&w=800&q=70",
  beverages:
    "https://images.unsplash.com/photo-1562114676-007657641f2e?auto=format&fit=crop&w=800&q=70",
  sweets:
    "https://images.unsplash.com/photo-1695568180070-8b5acead5cf4?auto=format&fit=crop&w=800&q=70",
};

/** Daily-dish photos keyed by the sample item slug. */
export const DAILY_IMAGES: Record<string, string> = {
  "chicken-biryani":
    "https://images.unsplash.com/photo-1559528896-c5310744cce8?auto=format&fit=crop&w=800&q=70",
  "mutton-biryani":
    "https://images.unsplash.com/photo-1566749249285-1798d7a0e470?auto=format&fit=crop&w=800&q=70",
  "chicken-karahi":
    "https://images.unsplash.com/photo-1694579740719-0e601c5d2437?auto=format&fit=crop&w=800&q=70",
  "beef-nihari":
    "https://images.unsplash.com/photo-1512010151537-2cf5c638ad51?auto=format&fit=crop&w=800&q=70",
  "chicken-haleem":
    "https://images.unsplash.com/photo-1708782344071-c5382e27ef3e?auto=format&fit=crop&w=800&q=70",
  "aloo-gosht":
    "https://images.unsplash.com/photo-1596797038530-2c107229654b?auto=format&fit=crop&w=800&q=70",
  "butter-chicken":
    "https://images.unsplash.com/photo-1742599361498-79824d24e355?auto=format&fit=crop&w=800&q=70",
  "chana-chaat":
    "https://images.unsplash.com/photo-1516714435131-44d6b64dc6a2?auto=format&fit=crop&w=800&q=70",
};

export function categoryImage(cat: Category | string): string | undefined {
  return CATEGORY_IMAGES[cat as Category];
}

/** Dish-type thumbnails for the standing menu (matched by keyword below). */
const DISH_IMAGES = {
  rice: "https://images.unsplash.com/photo-1630914441929-0d8ea69f95e6?auto=format&fit=crop&w=400&q=70",
  noodles:
    "https://images.unsplash.com/photo-1767324672583-8818531f650a?auto=format&fit=crop&w=400&q=70",
  chicken:
    "https://images.unsplash.com/photo-1708782344490-9026aaa5eec7?auto=format&fit=crop&w=400&q=70",
  fish: "https://images.unsplash.com/photo-1518732751612-2c0787ff5684?auto=format&fit=crop&w=400&q=70",
  meat: "https://images.unsplash.com/photo-1606843046080-45bf7a23c39f?auto=format&fit=crop&w=400&q=70",
  paneer:
    "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?auto=format&fit=crop&w=400&q=70",
  dal: "https://images.unsplash.com/photo-1755090154817-58d9d36ec988?auto=format&fit=crop&w=400&q=70",
  tea: "https://images.unsplash.com/photo-1491720731493-223f97d92c21?auto=format&fit=crop&w=400&q=70",
  coffee:
    "https://images.unsplash.com/photo-1645119917964-1b0763695ec7?auto=format&fit=crop&w=400&q=70",
  cola: "https://images.unsplash.com/photo-1636144832475-0d81f893354d?auto=format&fit=crop&w=400&q=70",
  lassi:
    "https://images.unsplash.com/photo-1692620609860-be6717812f71?auto=format&fit=crop&w=400&q=70",
  shake:
    "https://images.unsplash.com/photo-1583024012457-b6de05b7003a?auto=format&fit=crop&w=400&q=70",
  lemon:
    "https://images.unsplash.com/photo-1555949366-819808d99159?auto=format&fit=crop&w=400&q=70",
  gulab:
    "https://images.unsplash.com/photo-1593701461250-d7b22dfd3a77?auto=format&fit=crop&w=400&q=70",
  kheer:
    "https://images.unsplash.com/photo-1777613112969-d7511ddfbe15?auto=format&fit=crop&w=400&q=70",
  iceCream:
    "https://images.unsplash.com/photo-1724805053558-aea1220afbfa?auto=format&fit=crop&w=400&q=70",
  falooda:
    "https://images.unsplash.com/photo-1630823186728-b17c9f82a32d?auto=format&fit=crop&w=400&q=70",
  halwa:
    "https://images.unsplash.com/photo-1517244683847-7456b63c5969?auto=format&fit=crop&w=400&q=70",
} as const;

/** Best photo for a standing menu item, matched to its dish type by keyword. */
export function dishImage(name: string, category: Category | string): string | undefined {
  const n = name.toLowerCase();
  const has = (...ks: string[]) => ks.some((k) => n.includes(k));

  switch (category) {
    case "chinese":
      if (has("fried rice")) return DISH_IMAGES.rice;
      if (has("noodle")) return DISH_IMAGES.noodles;
      if (has("prawn", "shrimp")) return CATEGORY_IMAGES.seafood;
      if (has("fish")) return DISH_IMAGES.fish;
      if (has("beef", "meat", "mutton", "gosht")) return DISH_IMAGES.meat;
      if (has("gobi", "vegetable", "veg")) return CATEGORY_IMAGES.veg;
      return DISH_IMAGES.chicken;
    case "seafood":
      return has("fish", "pomfret") ? DISH_IMAGES.fish : CATEGORY_IMAGES.seafood;
    case "veg":
      if (has("paneer")) return DISH_IMAGES.paneer;
      if (has("dal")) return DISH_IMAGES.dal;
      if (has("chana", "chickpea")) return DAILY_IMAGES["chana-chaat"];
      return CATEGORY_IMAGES.veg;
    case "breads":
      return CATEGORY_IMAGES.breads;
    case "beverages":
      if (has("tea")) return DISH_IMAGES.tea;
      if (has("coffee")) return DISH_IMAGES.coffee;
      if (has("lassi")) return DISH_IMAGES.lassi;
      if (has("shake")) return DISH_IMAGES.shake;
      if (has("lemon")) return DISH_IMAGES.lemon;
      if (has("pepsi", "cola", "7up")) return DISH_IMAGES.cola;
      if (has("water", "mineral")) return undefined; // emoji is fine for water
      return CATEGORY_IMAGES.beverages; // mango/orange/cocktail/strawberry juice
    case "sweets":
      if (has("gulab", "jamun", "rasgulla")) return DISH_IMAGES.gulab;
      if (has("kheer")) return DISH_IMAGES.kheer;
      if (has("ice cream")) return DISH_IMAGES.iceCream;
      if (has("falooda")) return DISH_IMAGES.falooda;
      if (has("halwa")) return DISH_IMAGES.halwa;
      return CATEGORY_IMAGES.sweets; // rasmalai, mix sweets, laddu
    default:
      return undefined;
  }
}
